import { useMemo, useState } from 'react';
import { BaseButton } from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import PathfinderMobileQuestionLayout from './PathfinderMobileQuestionLayout';
import PathfinderMobileSelect from './PathfinderMobileSelect';

const EXPERIENCE_DURATION_OPTIONS = [
  { value: 1, label: '1 month' },
  { value: 3, label: '2~5 months' },
  { value: 6, label: '6~11 months' },
  { value: 12, label: '12~23 months' },
  { value: 24, label: '24~35 months' },
  { value: 36, label: '36 months or more' },
];

const EXPERIENCE_DURATION_VALUE_SET = new Set(
  EXPERIENCE_DURATION_OPTIONS.map((option) => option.value)
);

const parseConfig = (configJson) => {
  if (!configJson) return {};
  try {
    return typeof configJson === 'string' ? JSON.parse(configJson) : configJson;
  } catch {
    return {};
  }
};

const isDurationSelected = (durationValue) =>
  EXPERIENCE_DURATION_VALUE_SET.has(Number(durationValue));

const resolveMobileStep1Title = (title) =>
  title === 'What is your highest level of education?'
    ? 'What is highest level of education?'
    : title;

const resolveMobileStep1Helper = (helper) =>
  helper === 'Select the option that best describes your background'
    ? 'Select your highest completed degree'
    : helper;

const normalizeDurations = (durations = {}, validCodes = []) =>
  Object.fromEntries(
    Object.entries(durations).filter(
      ([code, months]) =>
        validCodes.includes(code) && isDurationSelected(months)
    )
  );

function DurationAccordion({
  option,
  durationValue,
  open,
  onToggle,
  onChange,
}) {
  const durationLabel = EXPERIENCE_DURATION_OPTIONS.find(
    (duration) => duration.value === Number(durationValue)
  )?.label;
  const ArrowIcon = open ? ArrowTopIcon : ArrowBottomIcon;

  return (
    <div className='flex w-full flex-col overflow-hidden rounded-[12px] border-[1.6px] border-grayscale-gray01'>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        typo={false}
        isStatic
        leftalign
        gap='gap-2'
        iconPosition='after'
        icon={
          <ArrowIcon className='size-4 shrink-0 text-text-tertiary' aria-hidden />
        }
        onClick={onToggle}
        textWrapperClass='h-auto min-w-0 flex-1 truncate text-left typo-b16Strong text-text-primary'
        className='h-12 w-full min-w-0 cursor-pointer items-center gap-2 rounded-none border-0 border-b-[1.6px] border-grayscale-gray01 bg-transparent py-[11px] pl-3 pr-4 text-left'
      >
        {durationLabel ? `${option.title} · ${durationLabel}` : option.title}
      </BaseButton>

      {open ? (
        <div className='flex flex-wrap gap-2 p-3'>
          {EXPERIENCE_DURATION_OPTIONS.map((duration) => (
            <PathfinderMobileSelect
              key={duration.value}
              size='chip'
              title={duration.label}
              state={Number(durationValue) === duration.value ? 'selected' : 'default'}
              onClick={() => onChange(duration.value)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PathfinderMobileExpBlockFlow({
  value,
  onChange,
  onComplete,
  onBack,
  configJson,
  config,
  options = [],
  progressLabel,
  loading = false,
  skipDurationStep = false,
}) {
  const expConfig = config || parseConfig(configJson);
  const {
    step1Title = 'What is highest level of education?',
    step1Helper = 'Select your highest completed degree',
    step2Title = 'Which types of experience related to your target role do you have?',
    step2Helper = 'Select all that apply',
    step1Options: step1Codes = [],
    step2Options: step2Codes = [],
    step2ExclusiveOption = 'EXP_NONE_TYPE',
  } = expConfig;

  const step1Options = useMemo(
    () =>
      options
        .filter((option) => step1Codes.includes(option.optionCode))
        .sort(
          (left, right) =>
            step1Codes.indexOf(left.optionCode) - step1Codes.indexOf(right.optionCode)
        )
        .map((option) => ({
          id: option.optionCode,
          title: option.optionLabel,
          caption: option.description ?? '',
          expScope: option.optionValue || option.optionCode,
        })),
    [options, step1Codes]
  );

  const step2Options = useMemo(
    () =>
      options
        .filter((option) => step2Codes.includes(option.optionCode))
        .sort(
          (left, right) =>
            step2Codes.indexOf(left.optionCode) - step2Codes.indexOf(right.optionCode)
        )
        .map((option) => ({
          id: option.optionCode,
          title: option.optionLabel,
          caption: option.description ?? '',
          expScope: option.optionValue || option.optionCode,
        })),
    [options, step2Codes]
  );

  const step2OptionMap = useMemo(
    () => new Map(step2Options.map((option) => [option.id, option])),
    [step2Options]
  );

  const initialDurations = useMemo(
    () =>
      normalizeDurations(
        value?.step2Durations,
        step2Options
          .map((option) => option.id)
          .filter((optionId) => optionId !== step2ExclusiveOption)
      ),
    [step2ExclusiveOption, step2Options, value?.step2Durations]
  );

  const [subStep, setSubStep] = useState(() => {
    if (!value?.step1) return 1;
    if (!Array.isArray(value?.step2) || value.step2.length === 0) return 2;
    if (value.step2.includes(step2ExclusiveOption) || skipDurationStep) return 2;
    return 3;
  });
  const [step1Value, setStep1Value] = useState(value?.step1 || null);
  const [step2Values, setStep2Values] = useState(
    Array.isArray(value?.step2) ? value.step2 : []
  );
  const [durations, setDurations] = useState(initialDurations);
  const [openCode, setOpenCode] = useState(() => {
    const selected = Array.isArray(value?.step2) ? value.step2 : [];
    return (
      selected.find(
        (code) =>
          code !== step2ExclusiveOption && !isDurationSelected(initialDurations[code])
      ) ??
      selected.find((code) => code !== step2ExclusiveOption) ??
      null
    );
  });

  const selectedExperienceCodes = step2Values.filter(
    (code) => code !== step2ExclusiveOption
  );

  const buildExperiences = (selectedCodes = step2Values, nextDurations = durations) =>
    selectedCodes
      .filter((code) => code !== step2ExclusiveOption)
      .map((code, index) => {
        const option = step2OptionMap.get(code);
        return {
          expScope: option?.expScope ?? code,
          expRelevant: true,
          expMonths: isDurationSelected(nextDurations[code])
            ? Number(nextDurations[code])
            : null,
          displayOrder: index,
        };
      });

  const buildValue = (
    selectedCodes = step2Values,
    nextDurations = durations,
    complete = false
  ) => ({
    step1: step1Value,
    step2: selectedCodes,
    step2Durations: nextDurations,
    complete,
    experiences:
      selectedCodes.includes(step2ExclusiveOption) || skipDurationStep
        ? []
        : buildExperiences(selectedCodes, nextDurations),
  });

  const handleStep1Select = (optionId) => {
    setStep1Value(optionId);
    onChange?.({
      step1: optionId,
      step2: step2Values,
      step2Durations: durations,
      complete: false,
      experiences: [],
    });
  };

  const handleStep2Change = (optionId) => {
    const isSelected = step2Values.includes(optionId);
    let nextValues;

    if (optionId === step2ExclusiveOption) {
      nextValues = isSelected ? [] : [optionId];
    } else {
      const withoutExclusive = step2Values.filter(
        (code) => code !== step2ExclusiveOption
      );
      nextValues = isSelected
        ? withoutExclusive.filter((code) => code !== optionId)
        : [...withoutExclusive, optionId];
    }

    const nextDurations = normalizeDurations(durations, nextValues);
    setStep2Values(nextValues);
    setDurations(nextDurations);
    onChange?.(buildValue(nextValues, nextDurations, false));
  };

  const handleDurationChange = (code, durationValue) => {
    const nextDurations = {
      ...durations,
      [code]: durationValue,
    };
    setDurations(nextDurations);
    setOpenCode(null);
    onChange?.(buildValue(step2Values, nextDurations, false));
  };

  const allDurationsComplete =
    selectedExperienceCodes.length > 0 &&
    selectedExperienceCodes.every((code) => isDurationSelected(durations[code]));

  const handleBack = () => {
    if (subStep === 1) {
      onBack?.();
      return;
    }
    setSubStep((current) => current - 1);
  };

  const handleNext = () => {
    if (subStep === 1) {
      if (!step1Value) return;
      setSubStep(2);
      return;
    }

    if (subStep === 2) {
      if (step2Values.length === 0) return;
      if (step2Values.includes(step2ExclusiveOption) || skipDurationStep) {
        onComplete?.(buildValue(step2Values, durations, true));
        return;
      }
      setOpenCode(selectedExperienceCodes[0] ?? null);
      setSubStep(3);
      return;
    }

    if (allDurationsComplete) {
      onComplete?.(buildValue(step2Values, durations, true));
    }
  };

  const title = subStep === 1 ? resolveMobileStep1Title(step1Title) : step2Title;
  const helperText =
    subStep === 1
      ? resolveMobileStep1Helper(step1Helper)
      : subStep === 2
      ? step2Helper
      : 'How long have you been involved in each experience?';
  const nextDisabled =
    loading ||
    (subStep === 1
      ? !step1Value
      : subStep === 2
      ? step2Values.length === 0
      : !allDurationsComplete);

  return (
    <PathfinderMobileQuestionLayout
      progressLabel={progressLabel}
      title={title}
      helperText={helperText}
      onBack={handleBack}
      onNext={handleNext}
      nextDisabled={nextDisabled}
    >
      {subStep === 1 ? (
        <div className='flex flex-col gap-3'>
          {step1Options.map((option) => (
            <PathfinderMobileSelect
              key={option.id}
              icon={null}
              iconPosition='none'
              textAlign='left'
              title={option.title}
              caption={option.caption}
              state={step1Value === option.id ? 'selected' : 'default'}
              onClick={() => handleStep1Select(option.id)}
            />
          ))}
        </div>
      ) : null}

      {subStep === 2 ? (
        <div className='flex flex-wrap gap-2'>
          {step2Options.map((option) => (
            <PathfinderMobileSelect
              key={option.id}
              size='chip'
              title={option.title}
              state={step2Values.includes(option.id) ? 'selected' : 'default'}
              onClick={() => handleStep2Change(option.id)}
            />
          ))}
        </div>
      ) : null}

      {subStep === 3 ? (
        <div className='flex flex-col gap-8'>
          {selectedExperienceCodes.map((code) => {
            const option = step2OptionMap.get(code);
            if (!option) return null;

            return (
              <DurationAccordion
                key={code}
                option={option}
                durationValue={durations[code]}
                open={openCode === code}
                onToggle={() => setOpenCode((current) => (current === code ? null : code))}
                onChange={(durationValue) => handleDurationChange(code, durationValue)}
              />
            );
          })}
        </div>
      ) : null}
    </PathfinderMobileQuestionLayout>
  );
}

export default PathfinderMobileExpBlockFlow;
